import { readItemField } from './item-field-reader';
import type { ItemFieldRow, ItemFieldDependencies } from './item-field-reader';
import { readCreatureStatuses } from './creature-status-reader';
import { phpJsonString } from './php-json-values';
import { putIntegerCompilePath } from './integer-compute-slots';
import { createInventoryTemplateIdentityResolver } from './inventory-template-identity';
import type { InventoryTemplateQuery } from './inventory-template-identity';
import { createStatusLayerReadContext } from './status-row-construction';
import type { StatusOriginLayerDependencies } from './status-row-construction';

const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const own = (row: unknown,key: string): unknown => arrayLike(row) && Object.prototype.propertyIsEnumerable.call(row,key)
    ? (row as Readonly<Record<string,unknown>>)[key] : undefined;
function working(value: unknown): unknown {
    return arrayLike(value) ? Object.fromEntries(Object.entries(value).map(([key,child])=>[key,working(child)])) : value;
}
function clone(value: unknown): unknown {
    if (!arrayLike(value)) return value ?? null;
    const entries = Object.entries(value);
    return entries.every(([key],i)=>key===String(i)) ? entries.map(([,child])=>clone(child))
        : Object.fromEntries(entries.map(([key,child])=>[key,clone(child)]));
}
export interface InventoryKeyLinksReaders {
    /** The GM card displays saved ties even when this row is an unlocked target of a separate lock. */
    includeAllLinks?: boolean;
    lockedId: string;
    itemField: (row: ItemFieldRow | null,key: string) => unknown;
    statuses: (row: ItemFieldRow) => ItemFieldRow;
    layerValue: (row: ItemFieldRow | null,key: string) => unknown;
    fetchLinks: (filters: ItemFieldRow) => unknown;
    invalidFetchIteration?: () => void;
}
/** Display-only saved ties; this compiler grants no permissions or key-use capability. */
export function compileInventoryKeyLinks(input: ItemFieldRow,inventoryId: unknown,readers: InventoryKeyLinksReaders): ItemFieldRow {
    if (!arrayLike(input)) throw new TypeError('Key link compile requires a PHP array');
    const row = working(input) as Record<string,unknown>,type = readers.itemField(row,'item_type');
    let locked = false;
    if (!readers.includeAllLinks) for (const status of Object.values(readers.statuses(row))) {
        if (status !== null && !arrayLike(status)) throw new TypeError('Status item reader requires a PHP array or null');
        const id = readers.itemField(status,'item_id') ?? own(status,'item_id') ?? '';
        if (id === readers.lockedId) { locked = true; break; }
    }
    function links(side: 'lock' | 'key',slot: 'unlocked_by' | 'opens',other: 'key' | 'lock'): void {
        const fetched = readers.fetchLinks({['inventory_key_link_'+side+'_id']:clone(inventoryId),inventory_key_link_deleted:0});
        if (!arrayLike(fetched)) { readers.invalidFetchIteration?.(); return; }
        const values: unknown[] = [];
        for (const link of Object.values(fetched)) {
            if (link !== null && !arrayLike(link)) throw new TypeError('Link layer reader requires a PHP array or null');
            values.push(readers.layerValue(link,'inventory_key_link_'+other+'_id'));
        }
        if (values.length === 0) return;
        const seen = new Set<string>(),unique = values.filter(value=>{
            const key = phpJsonString(value);
            if (seen.has(key)) return false;
            seen.add(key); return true;
        });
        putIntegerCompilePath(row,['computed','key_links',slot],working(unique));
    }
    if (readers.includeAllLinks || locked) links('lock','unlocked_by','key');
    if (readers.includeAllLinks || type === 'key') links('key','opens','lock');
    return clone(row) as ItemFieldRow;
}
export interface InventoryKeyLinksDependencies extends StatusOriginLayerDependencies, Omit<ItemFieldDependencies,'resolveCarrierRef'> {
    lockedId: string;
    queryTemplateDefinition?: InventoryTemplateQuery;
    fetchLinks: InventoryKeyLinksReaders['fetchLinks'];
}
export function createInventoryKeyLinksCompiler(deps: InventoryKeyLinksDependencies): (row: ItemFieldRow,id: unknown) => ItemFieldRow {
    const emit = (event: readonly unknown[]): void => { deps.trace?.(event); };
    const reference = (id: string): ItemFieldRow | null => {
        emit(['reference',id]);const value = deps.resolveCarrierRef(id);
        if (value !== null && !arrayLike(value)) throw new TypeError('Reference resolver must return a PHP array or null');
        return value;
    };
    const context = createStatusLayerReadContext(deps);
    const identity = deps.resolveInventoryIdentity ?? (deps.queryTemplateDefinition ? createInventoryTemplateIdentityResolver(deps.queryTemplateDefinition) : undefined);
    const readers: InventoryKeyLinksReaders = {lockedId:deps.lockedId,layerValue:context.layerValue,
        statuses:row=>readCreatureStatuses(row,'caster',reference),fetchLinks:deps.fetchLinks,
        invalidFetchIteration:()=>emit(['invalid-fetch-iteration']),itemField:(row,key)=>readItemField(row,key,{
            resolveCarrierRef:reference,
            ...(identity ? {resolveInventoryIdentity:(id:string)=>{emit(['inventory-identity',id]);return identity(id);}} : {}),
            ...(deps.resolveItemName ? {resolveItemName:(id:string)=>{emit(['name',id]);return deps.resolveItemName!(id);}} : {}),
        })};
    return (row,id)=>compileInventoryKeyLinks(row,id,readers);
}
